import { ControlsGroupInputProps, loadWrapper, PauseCover, useUnsavedChanges } from "aic";
import { AppSettingsContext } from "contexts/AppSettingsContext";
import { getHierarchyBaseUrl } from "contexts/hierarchy/getHierarchyBaseUrl";
import { ADD_HIERARCHY_NODES, SET_HIERARCHY_ID } from "contexts/hierarchy/interface";
import { HierarchyContext } from "contexts/HierarchyContext";
import { eAccessRights, eHierarchyLevel } from "functions/api/enums";
import { HierarchyCreateDto, saveHierarchyData } from "functions/api/hierarchy/saveHierarchyData";
import { IRequestStatus } from "functions/api/interface";
import { childHierarchyLevel } from "functions/childHierarchyLevel";
import { hierarchyNameValidator } from "functions/hierarchyNameValidator";
import { useContext, useState } from "react";
import { ExclamationCircle, PlusCircle } from "react-bootstrap-icons";
import { WordModal } from "../common/modals/WordModal";
import { NewReportModal } from "./NewReportModal";

/**
 * Button to add a new child hierarchy. Opens a modal to collect details,
 * validates input and calls the backend to create the new hierarchy node.
 */
export const AddHierarchyButton = (): React.ReactElement | null => {
  const { navigate } = useUnsavedChanges("add-hierarchy-button");
  const appSettings = useContext(AppSettingsContext);
  const hierarchyContext = useContext(HierarchyContext);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);

  // Current hierarchy information
  const nLevel = hierarchyContext?.hierarchy && childHierarchyLevel(hierarchyContext.hierarchy);
  const isReportLevel = nLevel === eHierarchyLevel.Report;

  // Modal inputs
  const modalInputs: ControlsGroupInputProps[] | null =
    hierarchyContext && nLevel
      ? [
          {
            inputLabel: "Name",
            inputName: "Name",
            validCondition: hierarchyNameValidator,
            validationRequestUrl: appSettings.backEndUrl,
            validationOptions: {
              existingValues:
                hierarchyContext.hierarchy?.children.map((c) => c.name) ??
                // istanbul ignore next
                [],
              otherValues: {
                level: nLevel,
                parentTag:
                  hierarchyContext.hierarchy?.hierarchyTag ??
                  // istanbul ignore next
                  "",
              },
            },
          },
          ...appSettings.defaultLevelProperties[nLevel]
            .filter((h) => h.mandatory)
            .map((p) => {
              // Always pass the parent hierarchy tag and sibling tags to scope uniqueness
              const parentTag =
                hierarchyContext.hierarchy?.hierarchyTag ??
                // istanbul ignore next
                "";

              return {
                inputLabel: p.label,
                inputName: p.key,
                validCondition: p.validCondition,
                validationOptions: {
                  existingValues:
                    hierarchyContext.hierarchy?.children.map((c) => c.hierarchyTag) ??
                    // istanbul ignore next
                    [],
                  otherValues: { parentTag },
                },
                validationRequestUrl: appSettings.backEndUrl,
              };
            }),
        ]
      : null;

  // Save request state
  const [saveRequest, setSaveRequest] = useState<IRequestStatus<HierarchyCreateDto>>({
    requesting: false,
    error: false,
  });

  /**
   * Add a new child hierarchy beneath the current hierarchy.
   *
   * @param newChildData - Keyed data for the new child hierarchy
   */
  const addChild = async (newChildData: { [key: string]: string }) => {
    /* istanbul ignore else */
    if (hierarchyContext && hierarchyContext.hierarchy && nLevel) {
      setShowAddModal(false);
      // Create new hierarchy
      const newChild: HierarchyCreateDto = {
        parentId: hierarchyContext.hierarchy.id,
        parentParentTag: hierarchyContext.hierarchy.parentTag,
        name: newChildData.Name.trim(),
        hierarchyTag: isReportLevel
          ? `${hierarchyContext.hierarchy.hierarchyTag}/Report/${newChildData.Name.trim()}`
          : (newChildData[
              appSettings.defaultLevelProperties[nLevel]?.find((p) => p.type === "hierarchyTag")
                ?.key ??
                // istanbul ignore next
                ""
            ] ??
            // istanbul ignore next
            crypto.randomUUID()),
        description:
          isReportLevel && newChildData.Description ? newChildData.Description.trim() : "",
        reportType:
          isReportLevel && newChildData["ReportType"] ? newChildData["ReportType"].trim() : "",
      };

      // Create new node
      const response = await loadWrapper(
        saveHierarchyData,
        newChild,
        saveRequest,
        setSaveRequest,
        appSettings.backEndUrl,
      );

      /* istanbul ignore else */
      if (response?.success) {
        hierarchyContext.dispatch({
          operation: ADD_HIERARCHY_NODES,
          hierarchyNodes: [response.data],
        });
        hierarchyContext.dispatch({
          operation: SET_HIERARCHY_ID,
          hierarchyId: response.data.id,
        });
        navigate(`${getHierarchyBaseUrl(response.data)}`);
      }
    }
  };

  return !hierarchyContext || !modalInputs ? null : (
    <>
      {hierarchyContext.accessRights?.includes(eAccessRights.CreateHierarchy) && (
        <div className="position-relative">
          {saveRequest.requesting && <PauseCover text="Saving..." />}
          {saveRequest.error && (
            <div>
              <ExclamationCircle className="pr-1" />
              {saveRequest.errorText ??
                // istanbul ignore next
                "Error adding hierarchy"}
            </div>
          )}
          <div
            id="hierarchylinks-add"
            onClick={() => {
              setShowAddModal(true);
            }}
          >
            <PlusCircle
              id="hierarchylinks-add-icon"
              aria-label={`Add new ${nLevel}`}
            />
            <span style={{ marginLeft: "0.5rem" }}>Add {nLevel}</span>
          </div>
          {showAddModal && (
            <>
              {isReportLevel ? (
                <NewReportModal
                  show={showAddModal}
                  onOk={addChild}
                  onCancel={() => {
                    setShowAddModal(false);
                  }}
                />
              ) : (
                <WordModal
                  id="hierarchylinks-add-modal"
                  show={showAddModal}
                  title={`Add new ${nLevel}`}
                  buttonLabel="Add"
                  onOk={addChild}
                  onCancel={() => {
                    setShowAddModal(false);
                  }}
                  inputs={modalInputs}
                />
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

AddHierarchyButton.displayName = "AddHierarchyButton";
